import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import ErrorState from '@wetransfer/transfer/component/ErrorState';
import getEnv from '@wetransfer/transfer/config/env';
import { InitialData } from '@wetransfer/transfer/helper/fetchInitialData';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';

import AppLoader from './component/AppLoader';

import './stylesheets/application.scss';
import { AUTH_CALLBACK_PATH } from './auth-bundle/constants';

interface Props {
  data: InitialData;
}

const DynamicApp = dynamic(() => import('./App'), {
  ssr: false,
  loading: () => AppLoader(),
});

const App = ({ data }: Props) => {
  const env = getEnv();

  const [isLoading, setIsLoading] = useState(
    // skip on SSR
    typeof window === 'undefined' ? false : true
  );

  const [isFailed] = useState(data === null);

  useEffect(() => {
    Cookie.configure({
      path: '/',
      domain: env.NEXT_PUBLIC_COOKIE_DOMAIN,
      secure: true,
    });

    setInitialState(data);
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <AppLoader />;
  }

  if (isFailed) {
    return <ErrorState />;
  }

  return <DynamicApp />;
};

const setInitialState = (data: Props['data']) => {
  if (data === null) {
    return;
  }

  const env = getEnv();

  // parse redirect_uri from query params for auth0 config
  const searchParams = new URLSearchParams(window.location.search);
  const redirect_uri = searchParams.get('redirect_uri');

  window.__consent_flags__ = data.metadata?.consentFlags ?? [];
  window.__session__ = {
    user: {
      id: false, // is always false because of plus legacy
      language: data.metadata?.languageDefaults.language,
      accepted_languages: data.metadata?.languageDefaults.acceptedLanguages,
      rules: {
        maximum_number_of_files: data.defaultRules?.maximumNumberOfFiles,
        maximum_number_of_recipients:
          data.defaultRules?.maximumNumberOfRecipients,
        storage_limit: data.defaultRules?.storageLimit,
        fair_use_storage_limit: null, // TODO: missing in response
        transfer_limit: data.defaultRules?.transferLimit,
        transfer_limit_for_default_recipient:
          data.defaultRules?.transferLimitForDefaultRecipient,
        can_password_protect_transfers:
          data.defaultRules?.canPasswordProtectTransfers,
        default_free_expiry: data.defaultRules?.defaultFreeExpiry,
        default_pro_expiry: data.defaultRules?.defaultProExpiry,
        default_expiry: data.defaultRules?.defaultExpiry,
        can_set_expiration: data.defaultRules?.canSetExpiration,
      },
    },
    ...(data.channel !== null && {
      profile: {
        subdomain_name: data.channel.subdomain,
        default_recipient_email: data.channel.receivingEmail,
      },
    }),
  };

  window.__recaptcha__ = {
    siteKey: env.NEXT_PUBLIC_RECAPTCHA_KEY,
    recaptchaOptions: {
      lang: 'en',
    },
  };
  window.__amplitude__ = {
    user: {
      key: data.metadata?.trackingId,
      country: data.metadata?.region.country,
      custom: {
        'is-we-transfer-staff': data.metadata?.isWetransferStaff,
      },
    },
  };
  window.__curated_wallpapers__ = data.config.curatedWallpapers;
  window.__stripe__ = {
    publishable_key: env.NEXT_PUBLIC_STRIPE_KEY,
  };
  window.__sus_bot__ = data.config.susbot;
  window.__auth0_config__ = {
    domain: env.NEXT_PUBLIC_AUTH_DOMAIN,
    audience: env.NEXT_PUBLIC_AUTH_AUDIENCE,
    client_id: env.NEXT_PUBLIC_AUTH_CLIENT_ID,
    redirect_uri:
      redirect_uri || `${window.location.origin}${AUTH_CALLBACK_PATH}`,
  };
  window.accountAppUrl = `https://${env.NEXT_PUBLIC_ACCOUNT_DOMAIN}`;
  window.portalsUrl = `https://${env.NEXT_PUBLIC_PORTALS_DOMAIN}`;
  window.checkoutUrl = `https://${env.NEXT_PUBLIC_CHECKOUT_DOMAIN}`;
  window.profitWellPublicAPIToken = env.NEXT_PUBLIC_PROFIT_WELL_API_TOKEN;
  window.__wallpaper_bundle__ = {
    microFrontendHost: env.NEXT_PUBLIC_WALLPAPER_MF_HOST,
  };
  window.customWallpaperServiceUrl =
    env.NEXT_PUBLIC_CUSTOM_WALLPAPER_SERVICE_URL;
};

export default App;
